import React, { useEffect, useState, useMemo } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Modal,
} from "react-bootstrap";
import DataTable, { TableColumn } from "react-data-table-component";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faRightLeft,
  faSpinner,
  faShuffle,
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import "./index.css";
import { toast } from "react-toastify";
import man from "../../assets/icons/man.PNG";
import { Controller, useForm } from "react-hook-form";
import { Input } from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { useAssignCartPersonsMutation, useGetCartSearchResultsMutation, useLazyGetAllCartDetailsQuery, useLazyGetAllCartPersonQuery, useNonAssignCartPersonsMutation } from "../../redux/services/cartSearchService";
import { setCartPersonsRowsPerPage } from "../../redux/cartPersonSlice";
import StatusBadge from "../../components/StatusBatch";
import ViewButtonComponent from "../../components/ViewButtonComponent";
import BreadCrumbs from "../../components/BreadCrumbs";
import ToggleSwitch from "../../components/ToggleSwitch";
import ButtonComponentV2 from "../../components/ButtonComponentV2";
import TableLoadingComponent from "../../components/TableLoadingComponent";

export interface CartPersonData {
  id: string;
  userId: string;
  displayName: string;
  phoneNumber: string;
  photoUrl: string;
  identityDocumentUrl: string;
  email: string;
  createdAt: {
    _seconds: number;
    _nanoseconds: number;
  };
  status: string;
  isAssignedCart?: boolean;
  canSelfRegisterCarts?: boolean;
  bankAccountDetails?: {
    accountName: string;
    accountNumber: string;
    bankName: string;
    branchName: string;
    swiftCode: string;
    createdAt: {
      _seconds: number;
      _nanoseconds: number;
    };
  };
  merchantId: string;
  canCollectCash: boolean;
}

interface OptionType {
  label: string;
  value: string;
}

export interface CartData {
  value: string;
  cartId: string;
  CartName: string;
}

const CartPersons = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [cartPersonList, setCartPersonList] = useState<CartPersonData[]>([]);
  const [showModal, setShowModal] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState<CartPersonData | null>(
    null
  );
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [cartsOptions, setCartsOptions] = useState<OptionType[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showAssignedOnly, setShowAssignedOnly] = useState<boolean>(() => {
    const savedStateCartPerson = localStorage.getItem("showAssignedOnly");
    return savedStateCartPerson ? JSON.parse(savedStateCartPerson) : false;
  });
  const [showSuggestions, setShowSuggestions] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isSearching, setIsSearching] = useState(false);
  const [searchOptions, setSearchOptions] = useState<CartData[]>([]);
  const [selectedCart, setSelectedCart] = useState(null);
  const [selectedCartName, setSelectedCartName] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("All");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const isAssignButtonDisabled = !selectedCart;
  const rowsPerPage = useSelector((state: any) => state.cartPersons.cartPersonsRowsPerPage);

  const {
    control,
  } = useForm<any>();

  const [
    triggerGetAllCartPersons,
    {
      data: cartPersons,
      isLoading: isCartPersonsLoading,
    },
  ] = useLazyGetAllCartPersonQuery();

  const [
    triggerGetAllCartss,
    { data: cartssData },
  ] = useLazyGetAllCartDetailsQuery();

  useEffect(() => {
    if (cartssData && Array.isArray(cartssData)) {
      const approvedCarts = cartssData.filter(
        (cart) => cart.status === "Approved"
      );
      const options = approvedCarts.map((cart) => ({
        label: cart.displayName,
        value: cart.id,
      }));
      setCartsOptions(options);
    }
  }, [cartssData]);

  useEffect(() => {
    triggerGetAllCartPersons("");
    triggerGetAllCartss("");
    if (cartPersons) {
      setCartPersonList(cartPersons);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartPersons, triggerGetAllCartPersons]);

  const handleViewCartDetails = (cartPerson: CartPersonData) => {
    navigate("/cartpersons/cartperson-list/cartperson-view", {
      state: { cartPerson },
    });
  };

  const handleOpenModal = (person: CartPersonData) => {
    setSelectedPerson(person);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const [nonAssignCartForCartPerson] = useNonAssignCartPersonsMutation();

  const [triggerCartSerachList] = useGetCartSearchResultsMutation();

  const handleSearch = async (inputValue: any) => {
    setIsSearching(true);
    if (inputValue.length > 1) {
      try {
        const obj = {
          query: `${inputValue}`,
          filters: "status:Approved AND isAssigned:false",
        }

        const response: any = await triggerCartSerachList(obj);

        if (response?.data) {
          const options = response.data.hits.map((item: any) => ({
            value: item.cartId,
            label: item.displayName,
          }));
          setSearchOptions(options);
        } else if (response?.errors) {
          toast.error("Something went wrong...! Please try again.");
        }
      } catch (error) {
        console.log("This is the error", error);
      }
    } else {
      setSearchOptions([]);
    }
    setIsSearching(false);
  };

  const handleRemove = () => {
    try {
      setIsLoading(true);
      const nonAssignObj = {
        cartPersonId: selectedPerson?.id,
      };
      nonAssignCartForCartPerson(nonAssignObj)
        .unwrap()
        .then(() => {
          triggerGetAllCartPersons("");
          handleCloseModal();
          toast.success("Cart Removed Successfully");
          setShowRemoveModal(false);
          setIsLoading(false);
        })
        .catch((error:any) => {
          setIsLoading(false);
          toast.error("Could not remove the cart");
        });
    } catch {
      setIsLoading(false);
      setShowRemoveModal(false);
    }
  };

  const [assignCartForCartPerson] = useAssignCartPersonsMutation();

  const handleAssign = async () => {
    try {
      setIsLoading(true);
       if (!selectedCart) {
      toast.error("Please select a cart from the list.");
      setIsLoading(false);
      return;
    }

    if (!selectedPerson || !selectedPerson.id) {
      toast.error("No cart person selected.");
      setIsLoading(false);
      return;
    }
      const assignObj = {
        // cartId: searchOptions[0].value,
        cartId: selectedCart ,
        cartPersonId: selectedPerson?.id,
      };

      assignCartForCartPerson(assignObj)
        .unwrap()
        .then(() => {
          triggerGetAllCartPersons("");
          handleCloseModal();
          toast.success("Cart Assinged Successfully");
          setIsLoading(false);
        })
        .catch((error:any) => {
          if (error.status === 409) {
            toast.error("This cart already assigned");
          } else {
            toast.error("Could not assign cart person");
          }
          setIsLoading(false);
        });
    } catch {
      handleCloseModal();
      setIsLoading(false);
    }
  };

  const handleToggleChange = (_menuId: string, checked: boolean) => {
    setShowAssignedOnly(checked);
    localStorage.setItem("showAssignedOnly", JSON.stringify(checked));
  };

  const statusOptions = useMemo(() => {
    const unique = Array.from(new Set(cartPersonList.map((p) => p.status).filter(Boolean)));
    return ["All", ...unique];
  }, [cartPersonList]);

  const filteredCartPersonList = cartPersonList.filter((person) => {
    const assignedCheck = showAssignedOnly ? person.isAssignedCart : !person.isAssignedCart;
    const statusCheck = statusFilter === "All" ? true : person.status === statusFilter;
    const nameCheck = person.displayName
      ? person.displayName.toLowerCase().includes(searchQuery.trim().toLowerCase())
      : false;
    return assignedCheck && statusCheck && nameCheck;
  });

  const handleChangeRowsPerPage = (newRowsPerPage: number) => {
    dispatch(setCartPersonsRowsPerPage(newRowsPerPage));
  };

  const columns: TableColumn<CartPersonData>[] = [
    {
      name: <h6 className="fw-bold">Photo</h6>,
      selector: (row) => row.photoUrl,
      cell: (row) => (
        <img
          src={row.photoUrl || man}
          alt={row.displayName}
          style={{
            width: "50px",
            height: "50px",
            borderRadius: "10px",
            margin: "5px",
          }}
          onError={(e) => {
            e.currentTarget.src = man;
          }}
        />
      ),
      sortable: false,
    },
    {
      name: <h6 className="fw-bold">Name</h6>,
      cell: (row) => (
        <div
          className="d-flex gap-2"
          style={{
            fontSize: "14px",
            fontWeight: "bold",
          }}
        >
          {row.displayName}
        </div>
      ),
      sortable: true,
    },
    {
      name: <h6 className="fw-bold">Status</h6>,
      cell: (row) => <StatusBadge status={row.status} />,
      sortable: true,
    },
    {
      name: <h6 className="fw-bold">Assign / UnAssign</h6>,
      cell: (row) => (
        <span
          className={
            row.isAssignedCart === true ? "text-success" : "text-danger"
          }
        >
          {row.isAssignedCart === true ? "Assigned" : "UnAssign"}
        </span>
      ),
      sortable: true,
    },
    {
      name: <h6 className="fw-bold">Action</h6>,
      selector: (row) => "action",
      cell: (row) => (
        <div className="d-flex gap-2 justify-content-end">
          <ViewButtonComponent onClick={() => handleViewCartDetails(row)} />
          &nbsp;
          <Button variant="light" onClick={() => handleOpenModal(row)}>
            <FontAwesomeIcon
              icon={row.isAssignedCart ? faRightLeft : faShuffle}
              color={row.isAssignedCart ? "#699BF7" : "#FF5733"}
            />
          </Button>
        </div>
      ),
      sortable: false,
    },
  ];

  const handleCreateCartPerson = () => {
    navigate("/cartpersons/cartperson-list/create-cartperson");
  };

  return (
    <>
      <BreadCrumbs hasSubMenus={true} />
      <div className="d-flex justify-content-between align-items-end">
        <div><h4>Cart Persons</h4></div>
        <div className="d-flex justify-content-end align-items-end">
          <div className="d-flex flex-column justify-content-end align-items-center m-2">
            <label className="text-center mb-2">
              {showAssignedOnly ? "Assigned" : "Unassigned"}
            </label>{" "}
            <ToggleSwitch
              menuId=""
              initialChecked={showAssignedOnly}
              onChange={handleToggleChange}
            />
          </div>
          <div className="d-flex align-items-end gap-2 m-2">
            <div style={{ minWidth: 160 }}>
              <Input
                type="select"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                disabled={isCartPersonsLoading}
              >
                {statusOptions.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </Input>
            </div>
            <div style={{ minWidth: 220 }}>
              <Input
                type="text"
                placeholder="Search by name"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                disabled={isCartPersonsLoading}
              />
            </div>
          </div>
          <div className="m-2">
            <ButtonComponentV2
              titel="Create Cart Person"
              onClick={handleCreateCartPerson}
              color="#fff"
              backgroundColor="#590B21"
            />
          </div>
        </div>
      </div>
      <Card>
        <CardHeader className="card-header">
          <div className="d-flex justify-content-between p-2">
            <h5 className="">Cart Persons</h5>
          </div>
          <hr />
        </CardHeader>
        <CardBody>
          {isCartPersonsLoading ? (
            <TableLoadingComponent numColumns={5} />
          ) : (
            <DataTable
              columns={columns}
              data={filteredCartPersonList}
              responsive
              pagination
              highlightOnHover
              pointerOnHover
              paginationPerPage={rowsPerPage}
              onChangeRowsPerPage={handleChangeRowsPerPage}
              noDataComponent={<div>No Cart Persons found</div>}
            />
          )}
        </CardBody>
      </Card>

      <Modal show={showModal} onHide={handleCloseModal} centered>
        <Modal.Body>
          {!selectedPerson?.isAssignedCart === false ? (
            <div className="d-flex row justify-content-center align-item-center mx-4">
              <div className=" col">
                {/* <h4 className="d-flex justify-content-center my-4 ">
                  Are you Sure?
                </h4> */}
                {/* <p className="d-flex remove-message-warning-paragraph">
                  This cart person is assigned to {selectedCartName ? selectedCartName : "a cart"}.
                </p> */}
                <p className="d-flex text-center remove-message-conform-paragraph mb-4 mt-4">
                  Do you want to unassign the cart from this cart person?
                </p>
                <div className="d-flex justify-content-center gap-3">
                  {isLoading ? (
                    <>
                      <ButtonComponentV2
                        titel="Removing..."
                        isDisabled={true}
                        color="#fff"
                        backgroundColor="#DC3545"
                        icon={<FontAwesomeIcon icon={faSpinner} spin />}
                      />
                      <ButtonComponentV2
                        titel="Cancel"
                        onClick={handleCloseModal}
                        isDisabled={true}
                        color="#fff"
                        backgroundColor="#9F9F9F"
                      />
                    </>
                  ) : (
                    <>
                      <ButtonComponentV2
                        titel="UnAssign"
                        onClick={handleRemove}
                        color="#fff"
                        backgroundColor="#DC3545"
                      />
                      <ButtonComponentV2
                        titel="Cancel"
                        onClick={handleCloseModal}
                        color="#fff"
                        backgroundColor="#9F9F9F"
                      />
                    </>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <>
              <h4 className="d-flex justify-content-center my-4 ">
                Are you Sure?
              </h4>
              <div className=" d-flex justify-content-center m-2 pb-2">
                you want to assign this person to the selected cart?
              </div>
              <Col lg="12" className="d-flex justify-content-center my-3 pb-3">
                <div className="autocomplete">
                  <Controller
                    name="ownerIdBySuperAdmin"
                    control={control}
                    rules={{ required: "Owner name is required." }}
                    render={({ field, fieldState: { error } }) => (
                      <>
                        <Input
                          {...field}
                          type="text"
                          name="ownerIdBySuperAdmin"
                          autoComplete="off"
                          placeholder="Enter Cart Name"
                          onChange={(e) => {
                            setSelectedCartName(e.target.value);
                            handleSearch(e.target.value);
                            setShowSuggestions(true);
                            setSelectedCart(null);
                          }}
                          value={selectedCartName}
                          disabled= {isLoading}
                        />
                        {error && (
                          <span className="validation-message text-danger">
                            {error.message}
                          </span>
                        )}
                      </>
                    )}
                  />
                  {showSuggestions && searchOptions.length > 0 && (
                    <div className="autocomplete-suggestions">
                      {searchOptions.map((option: any) => (
                        <div
                          key={option.value}
                          className="autocomplete-suggestion"
                          onClick={() => {
                            setSelectedCart(option.value);
                            setSelectedCartName(option.label);
                            setShowSuggestions(false);
                          }}
                        >
                          {option.label}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </Col>
              <div className="d-flex justify-content-center gap-3">
                {isLoading ? (
                  <>
                    <ButtonComponentV2
                      titel="Assiging..."
                      onClick={handleAssign}
                      color="#fff"
                      backgroundColor="#198754"
                      isDisabled={true}
                      icon={<FontAwesomeIcon icon={faSpinner} spin />}
                    />
                    <ButtonComponentV2
                      titel="Cancel"
                      onClick={handleCloseModal}
                      color="#fff"
                      backgroundColor="#9F9F9F"
                      isDisabled={true}
                    />
                  </>
                ) : (
                  <>
                    <ButtonComponentV2
                      titel="Assign"
                      onClick={handleAssign}
                      color="#fff"
                      backgroundColor="#198754"
                      isDisabled={isAssignButtonDisabled}
                    />
                    <ButtonComponentV2
                      titel="Cancel"
                      onClick={handleCloseModal}
                      color="#fff"
                      backgroundColor="#9F9F9F"
                    />
                  </>
                )}
              </div>
            </>
          )}
          <br />
        </Modal.Body>
      </Modal>
    </>
  );
};

export default CartPersons;
